/**
 * popupGate.js
 *
 * Tiny utility that prevents multiple site-level popups from showing at once.
 * Each popup checks `isOtherPopupOpen()` before calling setOpen(true) and, if
 * another popup is currently visible, schedules a re-check via `waitUntilIdle`.
 *
 * Priority order (enforced via the order of test-ids in BLOCKING_TESTIDS):
 *   1. cookie-consent-banner   (legal, always wins)
 *   2. zylvie-offer-modal      (special offer, every visit)
 *   3. mailing-list-popup      (timed, once per session)
 *   4. exit-intent-popup       (route-specific, once per session)
 */

const BLOCKING_TESTIDS = [
  "cookie-consent-banner",
  "zylvie-offer-modal",
  "mailing-list-popup",
  "exit-intent-popup",
];

/** Returns true if any blocking popup besides `selfTestId` is currently in DOM. */
export function isOtherPopupOpen(selfTestId = "") {
  if (typeof document === "undefined") return false;
  return BLOCKING_TESTIDS.some((id) => {
    if (id === selfTestId) return false;
    return Boolean(document.querySelector(`[data-testid='${id}']`));
  });
}

/**
 * Polls every `intervalMs` until no other popup is open, then calls cb().
 * Returns a cancel function. Stops automatically after `maxWaitMs`.
 */
export function waitUntilIdle(selfTestId, cb, { intervalMs = 600, maxWaitMs = 60000 } = {}) {
  if (typeof window === "undefined") return () => {};
  const startedAt = Date.now();
  let cancelled = false;

  const tick = () => {
    if (cancelled) return;
    if (!isOtherPopupOpen(selfTestId)) {
      cb();
      return;
    }
    if (Date.now() - startedAt > maxWaitMs) return; // give up silently
    window.setTimeout(tick, intervalMs);
  };

  tick();
  return () => { cancelled = true; };
}
